import React, { useEffect, useState } from "react";
import { PageHeader, Card, Tag } from "../components/UI";
import DataTable from "../components/DataTable";
import { Plus, X, UserCog, Trash2, Lock, Unlock } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth, ROLE_LABELS } from "../context/AuthContext";

const ROLES = ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"];

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "dse", territory: "", phone: "" });

  async function fetchUsers() {
    const { data } = await api.get("/users");
    setItems(data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/users");
        if (alive) setItems(data);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  async function submit(e) {
    e.preventDefault();
    try { await api.post("/users", form); setOpen(false); setForm({ email: "", name: "", password: "", role: "dse", territory: "", phone: "" }); await fetchUsers(); }
    catch (e) { alert(formatError(e)); }
  }

  async function del(id, e) {
    e?.stopPropagation();
    if (!window.confirm("Delete user?")) return;
    try { await api.delete(`/users/${id}`); await fetchUsers(); } catch (e) { alert(formatError(e)); }
  }

  async function toggleBlock(u, e) {
    e?.stopPropagation();
    const willBlock = u.status !== "disabled";
    const word = willBlock ? "BLOCK" : "UNBLOCK";
    const reason = willBlock ? window.prompt(`Reason for blocking ${u.name || u.email}? (optional)`) : null;
    if (willBlock && reason === null) return; // user cancelled prompt
    if (!window.confirm(`${word} login for ${u.name || u.email}?`)) return;
    try {
      await api.post(`/users/${u.id}/block`, { blocked: willBlock, reason: reason || undefined });
      await fetchUsers();
    } catch (e) { alert(formatError(e)); }
  }

  const canCreateSuper = user.role === "super_admin";
  const territories = [...new Set(items.map((u) => u.territory).filter(Boolean))].sort();

  const columns = [
    { key: "name", label: "Name", filter: "text" },
    { key: "email", label: "Email", filter: "text", render: (r) => <span className="font-mono text-[12px]">{r.email}</span> },
    { key: "role", label: "Role", filter: "select", options: ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] })),
      render: (r) => <Tag tone={r.role === "super_admin" ? "amber" : "teal"}><UserCog size={11} /> {ROLE_LABELS[r.role] || r.role}</Tag> },
    { key: "territory", label: "Territory", filter: "select", options: territories },
    { key: "status", label: "Status", filter: "select", options: ["active", "disabled"],
      render: (r) => <Tag tone={r.status === "disabled" ? "rose" : "emerald"}>{r.status === "disabled" ? "blocked" : "active"}</Tag> },
    { key: "created_at", label: "Created", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
    { key: "actions", label: "", sortable: false, align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={(e) => toggleBlock(r, e)}
            title={r.status === "disabled" ? "Unblock login" : "Block login"}
            className={`p-1 rounded ${r.status === "disabled" ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-500 hover:bg-rose-50 hover:text-rose-700"}`}
            data-testid={`toggle-block-${r.id}`}
          >
            {r.status === "disabled" ? <Unlock size={14} /> : <Lock size={14} />}
          </button>
          <button onClick={(e) => del(r.id, e)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-user-${r.id}`}><Trash2 size={14} /></button>
        </div>
      ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Administration"
        title="User Management"
        subtitle="Provision admins and field hierarchy users. Sort by any column, filter by role or territory, export to CSV."
        testId="users-page"
        actions={<button onClick={() => setOpen(true)} className="btn-primary flex items-center gap-2" data-testid="add-user-btn"><Plus size={14} /> Add user</button>}
      />

      <Card>
        <DataTable columns={columns} data={items} rowKey="id"
          searchableKeys={["name", "email", "territory"]} exportFilename="cbc-users" testIdPrefix="users"
          initialSort={{ key: "name", dir: "asc" }} />
      </Card>

      {open && (
        <div className="fixed inset-0 bg-slate-900/30 z-40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 w-full max-w-md p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
            <div className="flex items-center justify-between"><h3 className="serif-display text-2xl">New user</h3><button type="button" onClick={() => setOpen(false)}><X size={18} /></button></div>
            <div><label className="small-caps text-slate-500">Full name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><label className="small-caps text-slate-500">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div><label className="small-caps text-slate-500">Password</label><input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
            <div><label className="small-caps text-slate-500">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full">
                {ROLES.filter((r) => r !== "super_admin" || canCreateSuper).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <div><label className="small-caps text-slate-500">Territory</label><input value={form.territory} onChange={(e) => setForm({ ...form, territory: e.target.value })} /></div>
            <div><label className="small-caps text-slate-500">Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <button type="submit" className="btn-primary w-full">Create user</button>
          </form>
        </div>
      )}
    </div>
  );
}
